/**
 * 音声プレビューの「プレビュー条件」(サンプル条件) の状態管理フック — Issue #439。
 *
 * ## 何を解決するか
 *
 * プレビュー条件は
 *
 *   1. 個別フィールドのプレビュー (`AudioPreviewField`)
 *   2. コンテンツ全体・放送地点まるごとの通し試聴 (`BroadcastPointForm`)
 *
 * の **両方** から指定できる必要がある。両者で state 管理を複製すると
 * 「行の設定に追従する / 手入力を尊重する」という肝心のルールが片方だけ
 * 崩れてしまうため、本フックに一元化する。
 *
 * ## 解決規則 (最重要)
 *
 * - **ユーザーが編集済みのキー**: `sample` の値を使う (手動指定を尊重)。
 *   「12 分に変えて試聴」したのに行由来の 4 分で鳴ると意味がない。
 * - **未編集のキー**: `overrides` (編集中の行 / コンテンツ設定から自動導出した値) を
 *   優先する。行の遅れ分数を変えたらサンプル遅延も追従する。
 *
 * 送信値 (`resolve()`) と表示値 (`shown`) は同じ解決規則を通るため、
 * 「画面に出ている値と実際に鳴る音声」が食い違わない。
 */
import { useState } from 'react';

import type { PreviewSampleContext } from '../types';

export interface UsePreviewSampleResult {
  /** 条件パネルの開閉状態 */
  open: boolean;
  /** 条件パネルの開閉を切り替える */
  toggle: () => void;
  /**
   * 条件パネルに表示する値。
   * 未編集の項目は自動導出値 (`overrides`) を表示するため、
   * パネルを開いた時点で「何分の遅延で鳴るのか」が分かる。
   */
  shown: PreviewSampleContext;
  /** サンプル条件の 1 項目を更新し、「ユーザーが編集した」と記録する */
  update: <K extends keyof PreviewSampleContext>(key: K, value: PreviewSampleContext[K]) => void;
  /** 実際にサーバへ送るサンプル条件を解決する (表示値 `shown` と同じ規則) */
  resolve: () => PreviewSampleContext;
}

export function usePreviewSample({
  initial,
  overrides,
}: {
  /**
   * サンプル条件の初期値。
   * 凡例定義 (`buildSampleContext()` / `buildPointSampleContext()`) から作る。
   * 初回レンダー時のみ評価される (以降の変化は `overrides` 経由で反映する)。
   */
  initial: () => PreviewSampleContext;
  /**
   * 自動導出値。編集中の行 / コンテンツの設定値から毎レンダー算出したものを渡す。
   * **未編集のキーにのみ適用** されるため、ユーザーの手入力を壊さない。
   */
  overrides?: Partial<PreviewSampleContext>;
}): UsePreviewSampleResult {
  const [open, setOpen] = useState(false);
  const [sample, setSample] = useState<PreviewSampleContext>(initial);

  /*
   * ユーザーが手で編集したサンプル条件のキー集合。
   * 「未編集のキーだけ overrides を適用する」ために必要
   * (詳細はファイル冒頭の「解決規則」を参照)。
   */
  const [touchedKeys, setTouchedKeys] = useState<ReadonlySet<keyof PreviewSampleContext>>(
    () => new Set(),
  );

  const update = <K extends keyof PreviewSampleContext>(
    key: K,
    value: PreviewSampleContext[K],
  ): void => {
    setSample((prev) => ({ ...prev, [key]: value }));
    setTouchedKeys((prev) => new Set(prev).add(key));
  };

  const resolve = (): PreviewSampleContext => {
    const effective: PreviewSampleContext = { ...sample };
    for (const [key, value] of Object.entries(overrides ?? {})) {
      const typedKey = key as keyof PreviewSampleContext;
      if (value === undefined || touchedKeys.has(typedKey)) continue;
      (effective as Record<string, unknown>)[key] = value;
    }
    return effective;
  };

  return {
    open,
    toggle: () => setOpen((prev) => !prev),
    shown: resolve(),
    update,
    resolve,
  };
}
